import { useEffect, useState } from 'react';

import Button from '@components/atoms/button';
import FFHModal from '@components/modal';
import {
  BodyRegular,
  BodySmallRegular,
  H2
} from '@components/atoms/typography';

const MODAL_TITLE = "Update 'In the bank'";

const UPDATE_FAILED_MESSAGE =
  "We couldn't update your bank amount just now. Please try again in a few minutes.";

// Mirrors the app's bank edit dialog and the tenth-of-a-million granularity
// FPL prices use; a locale decimal comma is accepted.
const ONE_DECIMAL_PATTERN = /^\d*([.,]\d?)?$/;

// The squad budget cap; also the backend schema's upper bound for the field.
const MAX_BANK_MILLIONS = 100;

type UpdateBankModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The stored bank for the gameweek being edited, in millions. */
  initialBank: number;
  onUpdate: (bank: number) => Promise<void>;
  isUpdating: boolean;
};

export const UpdateBankModal = ({
  isOpen,
  onClose,
  initialBank,
  onUpdate,
  isUpdating
}: UpdateBankModalProps) => {
  const [inputValue, setInputValue] = useState(initialBank.toString());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-seed from the stored value each time the modal opens, so an abandoned
  // edit never leaks into the next session.
  useEffect(() => {
    if (isOpen) {
      setInputValue(initialBank.toString());
      setErrorMessage(null);
    }
  }, [isOpen, initialBank]);

  const parsedValue = parseFloat(inputValue.replace(',', '.'));
  const isValid =
    !Number.isNaN(parsedValue) &&
    parsedValue >= 0 &&
    parsedValue <= MAX_BANK_MILLIONS;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (ONE_DECIMAL_PATTERN.test(value)) {
      setInputValue(value);
    }
  };

  const handleClose = () => {
    if (isUpdating) return;
    setErrorMessage(null);
    onClose();
  };

  const handleUpdate = async () => {
    if (!isValid) return;
    setErrorMessage(null);
    try {
      await onUpdate(parsedValue);
      onClose();
    } catch {
      // Keep the modal open so the user can retry.
      setErrorMessage(UPDATE_FAILED_MESSAGE);
    }
  };

  return (
    <FFHModal open={isOpen} handleClose={handleClose} title={MODAL_TITLE}>
      <div
        className="flex flex-col gap-4 p-6 max-w-[500px]"
        data-cy="update-bank-modal"
      >
        <H2>{MODAL_TITLE}</H2>
        <BodyRegular textColor="text-neutral-700">
          If the amount is inaccurate, you can adjust it manually. Note that
          this will affect your transfer recommendations.
        </BodyRegular>

        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={handleChange}
            autoFocus
            aria-label="In the bank amount in millions"
            data-cy="update-bank-input"
            className="block w-24 rounded-lg border border-neutral-300 px-3 py-2 font-open-sans text-base tabular-nums placeholder-neutral-600 focus:border-primary-600 focus:outline-none"
          />
          <span className="font-open-sans text-base text-neutral-600">
            million
          </span>
        </div>

        {errorMessage && (
          <BodySmallRegular
            textColor="text-danger-700"
            data-cy="update-bank-error"
          >
            {errorMessage}
          </BodySmallRegular>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isUpdating}
            dataCy="update-bank-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={!isValid || isUpdating}
            dataCy="update-bank-confirm"
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </div>
      </div>
    </FFHModal>
  );
};
